import { AffiliateType } from "./AffiliateTypes"
import { GiftCardValidityResponseType } from "./GiftCardType"
import {
  CustomerTypeOrder,
  OrderChannelTypes,
  OrderPlatformTypes,
  PayOrderRequestType,
  ProductAvailabilityType,
  ProductFieldType,
  ProductMembershipType,
  ProductType,
} from "./OrderTypes"
import { ProductAddonType } from "./ProductTypes"

export type TipType = "percent" | "fixed"
export type TipSourceType = "operator" | "customer"

export interface GenericSuccessfulResponseType {
  id?: string
  message?: string
}

export interface CreateCartProductAddonType {
  product_id: string
  parent_id?: string
  cart_participant_id?: string
  quantities: {
    unit_id: string
    quantity: number
    amount?: number
  }[]
}
export interface CreateCartRequestType {
  items: {
    add_on?: CreateCartProductAddonType[]
    product_id: string
    parent_id?: string
    cart_participant_id?: string
    slot_id?: string
    quantities: {
      unit_id?: string
      quantity: number
      amount?: number
      booking_id?: string
    }[]
    availability_id?: string
    order_item_id?: string
    cart_item_id?: string
    is_updated?: number
    ignore_call_to_book_closure?: boolean
  }[]
  customer_id?: string
  affiliate_id?: string
  order_id?: string
  tips?: {
    type: TipType
    value: string
    source: TipSourceType
  }[]
  new_channel?: OrderChannelTypes
  new_platform?: OrderPlatformTypes
}
export interface LeviesType {
  id: string
  name: string
  amount_in_cents: number
  display_amount: string
  formatted_amount: string
  is_price_inclusive: boolean
  is_compounded: boolean
  type: number
  value: string
  type_name: string
  is_application_fee: boolean
  category: number
  category_name: string
}
export interface ParticipantsUnitType {
  id: string
  name: string
  description: string
  unit_type: string | null
  internal_name: string | null
  id_required: number
  seats_used: number
  capacity_used?: number
  pricing_structure_type?: string
  min_quantity?: number
  max_quantity?: number | null
  minimum_duration?: number
  maximum_duration?: number
  is_multi_day?: number
  has_flexible_timings?: number
  duration?: number
  is_associated?: boolean
  price_type?: string
  created_at: string
  updated_at: string
}
export interface CartQuantitiesType {
  id: string
  created_at: string
  updated_at: string
  quantity: number
  unit: ParticipantsUnitType | null
  price_in_cents: number
  display_price: number
  formatted_price: string
  price_type: string | null
  advertise_price: number | null
  max_quantity: number | null
  pricing_structure_type: string | null
  total_in_cents: number
  display_total: number
  formatted_total: string
}
export interface CartProductAvailabilityType extends ProductAvailabilityType {
  product_id: string
  tenant_id: string
  domain_id: string
  created_by: string
  updated_at: string
  deleted_at: string | null
}
export interface CartProductType extends ProductType {
  availability: CartProductAvailabilityType
  merchandise?: ProductAddonType["merchandise"]
  gift_card: GiftCardValidityResponseType["data"]
  minimum_participants: number
  minimum_participants_booking_display_message: string
  // Dynamic rental properties
  is_rental: boolean
  is_dynamic_rental: boolean
  is_multi_day_rental: boolean
  is_flexible_timings_rental: boolean
  has_addon: number
  minimum_giftcard_value: any
  // Membership properties
  membership: ProductMembershipType
}
export interface depositType {
  deposit_type: string
  deposit_amount: number
}
export interface CartItemType {
  created_at: string
  updated_at: string
  id: string
  slot_id: string | null
  product_availability_slot: {
    id: string
    created_at: string
    updated_at: string
    product_id: string
    tenant_id: string
    domain_id: string
    assets: string | null
    product_availability_id: string
    start_at: string
    end_at: string
    cancelled_at: string | null
    available: number
    booked: number
    hold: number
    local_start_time: string
    local_end_time: string
    availability: CartProductAvailabilityType
    bookings: {
      booking_number: string
      cancellation_reason: string | null
      cancelled_at: string | null
      cart_item_id: string
      check_in_date: string | null
      check_out_date: string | null
      checked_in: number
      created_at: string
      crew_count: number
      deleted_at: string | null
      domain_id: string
      id: string
      order_item_id: string
      product_id: string
      slot_id: string
      slot_uuid: string
      status: string
      tenant_id: string
      updated_at: string
    }[]
  } | null
  product_availability_slot_with_trashed: CartItemType["product_availability_slot"] | null
  slot_reserve_expires_at: string | null
  product: CartProductType
  quantities: CartQuantitiesType[]
  levies: LeviesType[]
  deposit?: {
    id: string
    created_at: string
    deposit_type: string
    deposit_amount: string
    amount: number
    formatted_amount: string
  }
  subtotal: number
  discounted_subtotal: number
  formatted_discounted_subtotal: string
  formatted_membership_total: string
  discount: number
  membership_discount: number
  membership_discount_json?: {
    id: string
    title: string
    amount: number
    amount_type: string // percentage
  }
  formatted_membership_discount: string
  total_discount: number
  display_subtotal: string
  formatted_subtotal: string
  total_levies: number
  display_total_levies: string
  formatted_total_levies: string
  total: number
  display_total: string
  formatted_total: string
  agreements: []
  parent_id?: string | null
  cart_participant_id: string | null
  description: string | null
}

export interface TipItemType {
  status: string
  value: string
  type: TipType
  source: TipSourceType
  amount_in_cents: number
  amount: number
  formatted_amount: string
  fee_in_cents: string
  fee: string
  formatted_fee: string
}
export interface GetCartResponseType {
  data: {
    id: string
    created_at: string
    updated_at: string
    items: CartItemType[]
    customer: CustomerTypeOrder
    total_with_tip: number
    formatted_total_with_tip: string
    total: number
    formatted_total: string
    deposit_with_tip: number
    formatted_deposit_with_tip: string
    subtotal: number
    formatted_subtotal: string
    discounted_subtotal: number
    formatted_discounted_subtotal: string
    discount: number
    membership_discount: number
    has_pending_booking: boolean
    total_discount: number
    deposit: number
    formatted_deposit: string
    formatted_discount: string
    formatted_membership_discount: string
    formatted_total_discount: string
    total_levies: number
    formatted_total_levies: string
    levies: LeviesType[]
    application_fee: string
    offer_code: string | null
    offer_id: string | null
    member_id: string
    is_deposit_enabled_for_items: boolean
    customerMembership: {
      id: string
      start_at: string
      end_at: string | null
      product: CartProductType
      code: string
      cancellation_date: string | null
      cancellation_reason: string | null
      is_active: number
      order: GetCartResponseType["data"]
    }
    deposit_auto_confirm: string
    full_amount_pay_now: {
      amount: number
      amount_formatted: string
      amount_in_units: number
      display_amount: string
    }
    full_amount_pay_later: {
      amount: number
      amount_formatted: string
      amount_in_units: number
      display_amount: string
    }
    deposit_pay_now: {
      amount: number
      amount_formatted: string
      amount_in_units: number
      display_amount: string
    }
    deposit_pay_later: {
      amount: number
      amount_formatted: string
      amount_in_units: number
      display_amount: string
    }
    payments: {
      total_paid: number
      remaining_balance: number
      pay_later: number
      formatted_pay_later: string
    }
    tips: TipItemType[]
    channel: string
    platform: string
    total_tip: number
    total_tip_formatted: string
    affiliate: AffiliateType
    has_confirm_booking?: boolean
    auto_apply_membership?: boolean
  }
}
export interface CreateCartResponseType {
  message: string
  cart: GetCartResponseType["data"]
}

export enum CheckInTypes {
  NOT_CHECKED_IN = "Not Checked In",
  CHECKED_IN = "Checked In",
  NO_SHOW = "No Show",
  CHECKED_OUT = "Checked Out",
}

export interface ParticipantsDetailsType {
  unit_id: string
  fields: {
    value: string | number | string[] | boolean
    product_field_id: string
  }[]
  add_on?: CreateCartProductAddonType[]
}
export interface AddParticipantsToCartRequestType {
  participants: Record<string, ParticipantsDetailsType[]>
}
export interface AddParticipantsToCartResponseType {
  message: string
  participants: {
    id: string
    unit: ParticipantsUnitType
    fields: ProductFieldType[]
  }[]
}
export interface GetParticipantsFromCart {
  data: {
    id: string
    unit: ParticipantsUnitType
    cart_item_id: string
    cart_id: string
    fields: ProductFieldType[]
  }[]
}

export enum PaymentTypes {
  DEPOSIT_AMOUNT = "deposit",
  FULL_AMOUNT = "full_payment",
  SAVE_CARD = "save_card",
}

export enum PosPaymentTypes {
  VISA = "VISA",
  MASTER_CARD = "MASTER_CARD",
  AMEX = "AMEX",
  CREDIT = "CREDIT",
  DEBIT = "DEBIT",
  CHEQUE = "CHEQUE",
  BANK_TRANSFER = "BANK_TRANSFER",
}

export enum EPosCardTypes {
  CREDIT = "CREDIT",
  INTERAC = "INTERAC",
}

export type UpdatePerOrderFieldsOfCartRequestType = {
  value: string | number | string[] | boolean
  product_field_id: string
}[]
export interface UpdatePerOrderFieldsOfCartResponseType {
  message: string
}

export interface ApplyPromocodeRequestType {
  offer_code: string
  payment_type?: string
}
export interface ApplyPromocodeResponseType extends CreateCartResponseType {
  code: number
  applicable_offer: Record<
    string,
    {
      code_type: string
      amount: string
      id: string
      min_quantity: number
    }
  >
  balance: ValidateGiftcardCodeResponseType["balance"]
  giftcard?: ValidateGiftcardCodeResponseType["giftcard"]
}

export interface PaymentMethodResponse {
  id: string
  object: string
  billing_details: BillingDetails
  card: Card
  created: number
  customer: null | string
  livemode: boolean
  type: string
}

export interface BillingDetails {
  address: Address
  email: null | string
  name: string
  phone: null | string
}

export interface Address {
  city: null | string
  country: null | string
  line1: null | string
  line2: null | string
  postal_code: null | string
  state: null | string
}

export interface Card {
  brand: string
  country: string
  exp_month: number
  exp_year: number
  funding: string
  generated_from: null
  last4: string
  wallet: null
}

export interface UpdateParticipantsInCartPayloadType {
  unit_id: string
  fields: {
    product_field_id: string
    value: string | number | string[] | boolean
  }[]
  add_on?: CreateCartProductAddonType[]
}
export interface UpdateParticipantsInCartRequestType {
  participants: Record<string, UpdateParticipantsInCartPayloadType[]>
}

export type PerBookingFieldType = {
  value: string | number | string[] | boolean
  product_field_id: string
  cart_item_id: string
}
export interface UpdatePerBookingFieldsOfCartRequestType {
  cartId: string
  cartItemId?: string
  body: PerBookingFieldType[]
}
export type UpdatePerBookingFieldsOfCartResponseType = {
  message: string
}
export type fieldOptionsType = {
  name: string | number
  label: string | number
}[]
export type PerBookingFieldTypeInBookingFields = {
  id: string
  created_at: string
  updated_at: string
  tenant_id: string
  domain_id: string
  cart_id: string | null
  level: string
  value: string | number | string[] | boolean
  label: string
  field_dump: {
    label: string
    value: string | number | string[] | boolean
    cart_item_id: string
    product_field: {
      id: string
      ask: string[]
      field: {
        id: string
        name: string
        type: string
      }
      label: string
      order: number
      value: string | number | string[] | boolean | null
      options: fieldOptionsType
      field_id: string
      required: boolean
      domain_id: string
      tenant_id: string
      created_at: string
      product_id: string
      updated_at: string
      placeholder: string
      validation_rules: string | null
      validation_message: string | null
      allowed_online_booking: boolean
      dont_show_during_order: boolean
      allowed_internal_booking: boolean
      allowed_affiliate_booking: boolean
      required_for_online_booking: number
      required_for_internal_booking: number
      required_for_affiliate_booking: number
    }
    cart_participant_id: string | null
  }
  order_id: string | null
  cart_item_id: string | null
  order_item_id: string | null
  cart_participant_id: string | null
  booking_participant_id: string | null
  field: {
    label: string
  }
}
export interface GetPerBookingFieldsOfCartResponseType {
  data: PerBookingFieldTypeInBookingFields[]
}

export interface GetPerOrderFieldsResponseType {
  data: {
    id: string
    created_at: string
    updated_at: string
    tenant_id: string
    domain_id: string
    cart_id: string
    level: string
    value: string
    label: string
    field_dump: {
      label: "First Name"
      value: string
    }
    order_id: string
    cart_item_id: string | null
    order_item_id: string | null
    field: {
      label: string
    }
  }[]
}

export interface ValidateGiftcardCodeResponseType extends CreateCartResponseType {
  code: number
  applicable_offer: Record<
    string,
    {
      code_type: string
      amount: string
      id: string
      min_quantity: number
    }
  >
  balance: number
  giftcard: {
    code?: string
    balance: {
      amount: number
      display_amount: string
      amount_in_units: number
      amount_formatted: string
    }
    remaining_total: {
      amount: number
      display_amount: string
      amount_in_units: number
      amount_formatted: string
    }
    remaining_balance: {
      amount: number
      display_amount: string
      amount_in_units: number
      amount_formatted: string
    }
    remaining_deposit_balance: {
      amount: number
      display_amount: string
      amount_in_units: number
      amount_formatted: string
    }
    remaining_deposit_total: {
      amount: number
      display_amount: string
      amount_in_units: number
      amount_formatted: string
    }
  }
}
export interface CreateEditExpenseDiscountRequestType {
  description: string
  amount: number
  cart_item_id?: string // For editing the expense
  order_item_id?: string // For editing the expense in edit order flow
  parent_id: string // Id of the corresponding cart item
  levies?: string[]
}

export interface CalculateOrderTipRequestType {
  tip: PayOrderRequestType["tip"]
}
export interface CalculateOrderTipResponseType {
  message: string
  response: {
    amount_to_pay: {
      amount: number
      display_amount: string
      amount_formatted: string
    }
    total_tip: {
      amount: number
      display_amount: string
      amount_formatted: string
    }
  }
}

export type UpdatePerOrderFieldsOfOrderRequestType = {
  id?: string
  field_id?: string
  value: string | number | string[] | boolean
}[]

export type UpdatePerBookingFieldsOfOrderRequestType = {
  id?: string
  field_id?: string
  order_item_id: string
  value: string | number | string[] | boolean
}[]
